import React, { useEffect, useState, useCallback } from 'react';
import {
  ChartLine,
  Warning,
  ArrowClockwise,
  Clock,
} from 'phosphor-react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import api from '../../lib/api';
import GeniLoader from '../../components/GeniLoader';
import { OptionsProLockedFeature } from '../../components/options/OptionsProLockedFeature';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ── Formatters ────────────────────────────────────────────────────────────────
function fmtPct(val) {
  if (val === null || val === undefined) return '—';
  return `${val.toFixed(1)}%`;
}

/**
 * Spread visual consistente com os dois cards anteriores.
 * Se DVOL exibe 36.0 e PredRV exibe 35.7, o usuário espera ver 0.3 (não 0.4).
 * Calculamos: round(dvol, 1) − round(predicted_rv30, 1).
 */
function fmtSpreadConsistent(dvol, predictedRv30) {
  if (dvol == null || predictedRv30 == null) return '—';
  const d = Math.round(dvol * 10) / 10;
  const p = Math.round(predictedRv30 * 10) / 10;
  const v = Math.round((d - p) * 10) / 10; // evita floating-point drift
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} pp`;
}

function fmtTimestamp(isoStr, isPortuguese) {
  if (!isoStr) return '—';
  try {
    const locale = isPortuguese ? 'pt-BR' : undefined;
    return new Date(isoStr).toLocaleString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return isoStr; }
}

// ── Custom Recharts Tooltip ───────────────────────────────────────────────────
function ChartTooltipContent({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#1A1A2E] border border-white/10 rounded-lg p-3 text-xs text-white/80 shadow-xl">
      <p className="font-semibold mb-1 text-white/50">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {Number(entry.value).toFixed(1)}%
        </p>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function OptionsVolatility() {
  const { isPortuguese } = useOptionsLocale();
  const { isPro, loading: entitlementsLoading } = useOptionsEntitlements();
  const [data, setData]       = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [currentRes, historyRes] = await Promise.all([
        api.get('/options/volatility/current'),
        isPro ? api.get('/options/volatility/history').catch(() => ({ data: { forecasts: [] } })) : Promise.resolve({ data: { forecasts: [] } }),
      ]);
      setData(currentRes.data);
      setHistory((historyRes.data?.forecasts || []).slice().reverse());
    } catch (err) {
      console.error('Volatility fetch error:', err);
      setError(
        isPortuguese
          ? 'Falha ao carregar os dados de volatilidade.'
          : 'Failed to load volatility data.'
      );
    } finally {
      setLoading(false);
    }
  }, [isPortuguese, isPro]);

  useEffect(() => {
    if (!entitlementsLoading) fetchData();
  }, [entitlementsLoading, fetchData]);

  const isStale       = data?.data_status === 'stale';
  const isUnavailable = data?.data_status === 'unavailable' || (!loading && !data);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <OptionsPageShell hideHeader>
        <div className="flex items-center justify-center min-h-[50vh]" data-testid="volatility-loading">
          <GeniLoader />
        </div>
      </OptionsPageShell>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error && !data) {
    return (
      <OptionsPageShell hideHeader>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 text-white/60"
          data-testid="volatility-error">
          <Warning size={32} weight="fill" className="text-amber-400" />
          <p className="text-sm">{error}</p>
          <button
            onClick={fetchData}
            className="flex items-center gap-1.5 text-xs border border-white/15 rounded-lg px-3 py-2
              text-white/50 hover:text-white/80 hover:border-white/30 transition-colors"
          >
            <ArrowClockwise size={13} /> {isPortuguese ? 'Tentar novamente' : 'Retry'}
          </button>
        </div>
      </OptionsPageShell>
    );
  }

  const hasHistoryData = history.length > 1;

  return (
    <OptionsPageShell hideHeader>
      <div
        className="min-h-[calc(100vh-180px)] w-full py-6 sm:py-8"
        data-testid="options-volatility-page"
      >
        <div className="w-full max-w-[860px] mx-auto space-y-7">

          {/* ── Header ───────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-2 border-b border-white/5 pb-5"
            data-testid="volatility-header">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]
              text-white/40">
              <ChartLine size={13} weight="bold" />
              <span>GENI OPTIONS</span>
            </div>
            <h1 className="text-3xl font-black tracking-tight text-white" data-typography="heading">
              {isPortuguese ? 'Volatilidade' : 'Volatility'}
            </h1>
            <p className="text-sm text-white/55 max-w-xl">
              {isPortuguese
                ? 'Volatilidade implícita atual versus volatilidade prevista para os próximos 30 dias.'
                : 'Current implied volatility versus predicted volatility for the next 30 days.'}
            </p>
          </div>

          {/* ── Stale warning ─────────────────────────────────────────────── */}
          {isStale && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5
              px-4 py-2.5 text-xs text-amber-400/80" data-testid="volatility-stale-banner">
              <Clock size={13} weight="fill" />
              {isPortuguese
                ? 'Usando último dado válido de volatilidade disponível.'
                : 'Using last available volatility data.'}
            </div>
          )}

          {/* ── Unavailable ───────────────────────────────────────────────── */}
          {isUnavailable && (
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/3
              px-4 py-2.5 text-xs text-white/40" data-testid="volatility-unavailable-banner">
              <Warning size={13} />
              {isPortuguese
                ? 'Dados temporariamente indisponíveis.'
                : 'Data temporarily unavailable.'}
            </div>
          )}

          {/* ── Three main cards ──────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="volatility-main-cards">

            {/* DVOL */}
            <Card className="bg-white/[0.03] border-white/8 rounded-xl">
              <CardContent className="p-5 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-widest text-white/40">
                  DVOL
                </div>
                <div className="text-4xl font-black tracking-tight text-white"
                  data-testid="dvol-value">
                  {fmtPct(data?.dvol)}
                </div>
                <p className="text-xs text-white/40 leading-relaxed">
                  {isPortuguese
                    ? 'Volatilidade implícita atual do BTC.'
                    : 'Current implied volatility of BTC.'}
                </p>
              </CardContent>
            </Card>

            {isPro ? (
              <Card className="bg-white/[0.03] border-white/8 rounded-xl">
                <CardContent className="p-5 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-widest text-white/40">{isPortuguese ? 'VOL PREVISTA 30D' : 'PREDICTED VOL 30D'}</div>
                  <div className="text-4xl font-black tracking-tight text-white" data-testid="predicted-rv30-value">{fmtPct(data?.predicted_rv30)}</div>
                  <p className="text-xs text-white/40 leading-relaxed">{isPortuguese ? 'Volatilidade realizada estimada para os próximos 30 dias.' : 'Estimated realized volatility for the next 30 days.'}</p>
                </CardContent>
              </Card>
            ) : (
              <OptionsProLockedFeature featureKey="VOLATILITY_FORECAST" title={isPortuguese ? 'Volatilidade Prevista 30D' : 'Predicted Vol 30D'} description={isPortuguese ? 'Previsão HAR+DVOL para os próximos 30 dias.' : 'HAR+DVOL forecast for the next 30 days.'} testId="volatility-forecast-pro-lock" compact />
            )}

            {isPro ? (
              <Card className="bg-white/[0.03] border-white/8 rounded-xl">
                <CardContent className="p-5 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-widest text-white/40">{isPortuguese ? 'SPREAD DE VOL' : 'VOL SPREAD'}</div>
                  <div className="text-4xl font-black tracking-tight text-white" data-testid="predicted-spread-value">{fmtSpreadConsistent(data?.dvol, data?.predicted_rv30)}</div>
                  <p className="text-xs text-white/40 leading-relaxed">{isPortuguese ? 'Diferença entre DVOL e Vol Prevista.' : 'Difference between DVOL and Predicted Vol.'}</p>
                </CardContent>
              </Card>
            ) : (
              <OptionsProLockedFeature featureKey="VOLATILITY_FORECAST" title={isPortuguese ? 'Spread de Volatilidade' : 'Volatility Spread'} description={isPortuguese ? 'Diferença entre DVOL e a volatilidade prevista.' : 'Difference between DVOL and predicted volatility.'} testId="volatility-spread-pro-lock" compact />
            )}
          </div>

          {/* ── Timestamp ─────────────────────────────────────────────────── */}
          {data?.timestamp && (
            <p className="text-[11px] text-white/25 text-right" data-testid="volatility-timestamp">
              {isPortuguese ? 'Atualizado em' : 'Updated at'}{' '}
              {fmtTimestamp(data.timestamp, isPortuguese)}
            </p>
          )}

          {/* ── Historical chart ───────────────────────────────────────────── */}
          {isPro ? <div className="space-y-3" data-testid="volatility-history-section">
            <h2 className="text-sm font-semibold text-white/60 uppercase tracking-widest">
              {isPortuguese ? 'Histórico de Previsões' : 'Forecast History'}
            </h2>

            {hasHistoryData ? (
              <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={history} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="forecast_date"
                      tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
                      tickFormatter={(v) => v?.slice(5) || v}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <RechartsTooltip content={<ChartTooltipContent />} />
                    <Legend
                      wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="dvol"
                      name="DVOL"
                      stroke="#45B7D1"
                      dot={false}
                      strokeWidth={1.5}
                    />
                    <Line
                      type="monotone"
                      dataKey="predicted_rv30"
                      name={isPortuguese ? 'Vol Prevista 30D' : 'Predicted Vol 30D'}
                      stroke="#A78BFA"
                      dot={false}
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="rounded-xl border border-white/5 bg-white/[0.01] p-6 text-center
                text-xs text-white/30" data-testid="volatility-history-empty">
                {isPortuguese
                  ? 'O histórico será exibido conforme novas previsões forem registradas.'
                  : 'History will appear as new forecasts are recorded.'}
              </div>
            )}
          </div> : (
            <OptionsProLockedFeature
              featureKey="VOLATILITY_FORECAST"
              title={isPortuguese ? 'Histórico de Previsões' : 'Forecast History'}
              description={isPortuguese ? 'Acompanhe a evolução das previsões e do DVOL ao longo do tempo.' : 'Track forecasts and DVOL over time.'}
              testId="volatility-history-pro-lock"
              compact
            />
          )}

        </div>
      </div>
    </OptionsPageShell>
  );
}
